import { aplicarVelo } from '../utils/velo.js';
import { baseApiConGemela } from '../utils/base-publica.js';

// --- CACHÉ DE SESIÓN (Map a nivel de módulo) ---
// Evita peticiones duplicadas para la misma query durante la sesión activa.
// Con 8 bloques con imagen, el segundo render tiene 0 fetches adicionales.
const _imgCache = new Map();
const _pendingFetches = new Map(); // Deduplicar peticiones concurrentes

// --- CACHÉ NEGATIVA (query → instante hasta el que no se vuelve a pedir) ---
// Una búsqueda que el servidor contesta BIEN pero sin foto (`{url: ''}`) se volvía a pedir en
// cada repintado: la caché de arriba solo guarda aciertos. El servidor ya guarda esa respuesta
// (`core_services/unsplash.py`: 5 min si no hay resultados, `_NEG_TTL` = 1 min si Unsplash falló),
// así que repetir la petición antes de un minuto solo trae el mismo vacío. Solo se apunta con un
// 200: un 429, un 5xx o un fallo de red siguen sin apuntarse, que es lo que reintenta el barrido
// diferido de abajo.
const _SIN_FOTO_MS = 60000;
const _sinFoto = new Map();

// --- REINTENTO DIFERIDO ---
// Cuántas veces se vuelve a barrer la página buscando imágenes que se quedaron sin resolver, y
// cuánto se espera entre barridos. Existe porque rendirse era definitivo: una foto que fallaba
// —por un pico de peticiones al generar, o porque Unsplash tardó— se quedaba en el placeholder
// para siempre, y la única forma de recuperarla era recargar. El barrido reusa
// `resolverImagenesUnsplash` entera en vez de guardar los `<img>` que fallaron: los que siguen sin
// resolver conservan su keyword en `data-unsplash` (solo el ÉXITO la reescribe con la URL), así
// que volver a seleccionarlos es exacto y no hay estado que mantener.
const _MAX_RONDAS_DIFERIDAS = 2;
const _ESPERA_DIFERIDA_MS = 15000;
let _rondasDiferidas = 0;
let _reintentoArmado = false;

async function _fetchFoto(query) {
    if (_imgCache.has(query)) return _imgCache.get(query);
    const hasta = _sinFoto.get(query);
    if (hasta && Date.now() < hasta) return null;
    // Si ya hay una petición en vuelo para esta query, reutilizarla
    if (_pendingFetches.has(query)) return _pendingFetches.get(query);

    const doFetch = async (retries = 3, delay = 1500) => {
        try {
            // Editor → el del builder; SPA pública y publicado → la gemela pública (base-publica.js).
            const res = await fetch(`${baseApiConGemela()}/api/buscar-foto/?query=${encodeURIComponent(query)}`);
            if (res.status === 429) {
                if (retries > 0) {
                    await new Promise(r => setTimeout(r, delay));
                    return doFetch(retries - 1, delay * 2);
                }
                console.warn(`[Unsplash] Rate limited para "${query}", reintentando más tarde.`);
                return null;
            }
            const data = await res.json();
            const url = data.url || null;
            if (url) _imgCache.set(query, url);
            else if (res.ok) _sinFoto.set(query, Date.now() + _SIN_FOTO_MS);
            return url;
        } catch (e) {
            console.error('[Unsplash] Error al obtener imagen:', e);
            return null;
        }
    };

    const promise = doFetch();
    _pendingFetches.set(query, promise);
    const result = await promise;
    _pendingFetches.delete(query);
    return result;
}

// --- ¿LA QUERY YA ES UNA URL RESUELTA? ---
// SSOT de la heurística "esto ya es una URL, no una keyword de búsqueda".
// La usan cargar() y el resolvedor de fondos para cortar ANTES de _fetchFoto().
// No dupliques este startsWith en otro sitio.
const isResolved = (q) => !q || q.startsWith('http') || q.startsWith('/') || q.startsWith('data:');

// --- RESOLVEDOR DE IMÁGENES UNSPLASH ---
/**
 * @param {object} [opts]
 * @param {boolean} [opts.diferido] Lo pone SOLO el barrido de reintento. Sirve para dos cosas:
 *   no reiniciar el contador de rondas (o el reintento se llamaría a sí mismo sin fin) y saber que
 *   esta pasada no viene de un render nuevo.
 */
export async function resolverImagenesUnsplash(opts) {
    const diferido = !!(opts && opts.diferido);
    // Una pasada NUEVA (viene de un render) reabre el cupo de reintentos: es otra página, u otro
    // conjunto de bloques, y merece sus propias oportunidades.
    if (!diferido) _rondasDiferidas = 0;

    const targets = document.querySelectorAll('.unsplash-target');
    const bgTargets = document.querySelectorAll('.unsplash-bg-target');
    // Lo pone `cargar()` cuando una foto se queda sin resolver. Gobierna el barrido diferido.
    let fallaron = false;

    // OJO: aquí NO va un early-exit del tipo "si todo está resuelto, return".
    // Lo hubo y era un bug: las <img> nacen con opacity:0 (para el fundido al
    // resolver) y los fondos se pintan por JS, así que retornar no se saltaba
    // solo el fetch — se saltaba el REVELADO, que no es opcional. Con un sitio
    // guardado (todas las queries ya son URLs) las imágenes quedaban invisibles,
    // el fondo del hero no se pintaba y el "Cargando activo visual..." era eterno.
    // La optimización de no llamar a la API SÍ se conserva, pero donde debe estar:
    // cargar() y el resolvedor de fondos cortan por isResolved() antes de _fetchFoto().

    const cargar = async (img) => {
        const query = img.getAttribute('data-unsplash');
        if (!query) return;

        const container = img.closest('.features-image-side, .content-image-side, .hero-image-side');
        const textSide = img.closest('section')?.querySelector('.features-text-side, .content-text-side, .hero-text-side');
        const loadingLabel = container?.querySelector('.loading-label');

        // Si ya es una URL resuelta, usarla directamente sin llamar a la API
        if (isResolved(query)) {
            img.src = query;
            img.style.opacity = "1";
            const caption = img.closest('.sortable-item')?.querySelector('.unsplash-caption');
            if (caption) caption.style.opacity = "1";
            
            // Sincronizar clones de Splide
            document.querySelectorAll(`.splide__slide--clone img[data-unsplash="${query}"]`).forEach(cloneImg => {
                cloneImg.src = query;
                cloneImg.style.opacity = "1";
                const cloneCaption = cloneImg.closest('.splide__slide--clone')?.querySelector('.unsplash-caption');
                if (cloneCaption) cloneCaption.style.opacity = "1";
            });

            if (container) container.style.display = "block";
            if (textSide) textSide.style.flex = "1.2";
            if (loadingLabel) loadingLabel.remove();
            return;
        }

        // Usar helper con caché — no hay fetch si la query ya fue resuelta
        const url = await _fetchFoto(query);
        if (url) {
            img.src = url;
            img.style.opacity = "1";
            // Persistir URL resuelta → al guardar, extractor guarda URL en vez de keyword
            // Al recargar, la URL pasa por el check startsWith('http') → skip fetch
            img.setAttribute('data-unsplash', url);
            const caption = img.closest('.sortable-item')?.querySelector('.unsplash-caption');
            if (caption) caption.style.opacity = "1";
            
            // Sincronizar clones de Splide
            document.querySelectorAll(`.splide__slide--clone img[data-unsplash="${query}"]`).forEach(cloneImg => {
                cloneImg.src = url;
                cloneImg.style.opacity = "1";
                cloneImg.setAttribute('data-unsplash', url);
                const cloneCaption = cloneImg.closest('.splide__slide--clone')?.querySelector('.unsplash-caption');
                if (cloneCaption) cloneCaption.style.opacity = "1";
            });

            if (container) container.style.display = "block";
            if (textSide) textSide.style.flex = "1.2";
            if (loadingLabel) loadingLabel.remove();
        } else {
            // Solo eliminar el contenedor si es un bloque con layout específico (features/content/hero)
            // Para gallery u otros, simplemente dejar el placeholder visible
            if (container) {
                if (loadingLabel) loadingLabel.remove();
                container.remove();
                if (textSide) {
                    textSide.style.flex = "1";
                    textSide.style.maxWidth = "850px";
                    textSide.style.margin = "0 auto";
                    textSide.style.textAlign = "center";
                }
            } else {
                // Sin container específico (galería, etc.): se enseña el placeholder ENTERO.
                // Estaba al 40 % y ese medio tono era el síntoma que se reportaba como "las fotos
                // de la galería no cargan": un gris difuminado no comunica nada, parece la web rota
                // en vez de una imagen que aún no está. A opacidad plena se lee como lo que es —un
                // hueco a la espera— y, si el barrido diferido de abajo la consigue, entra con su
                // fundido normal.
                img.style.opacity = "1";
                fallaron = true;
                const caption = img.closest('.sortable-item')?.querySelector('.unsplash-caption');
                if (caption) caption.style.opacity = "1";

                // Sincronizar clones de Splide (fallo de carga)
                document.querySelectorAll(`.splide__slide--clone img[data-unsplash="${query}"]`).forEach(cloneImg => {
                    cloneImg.style.opacity = "1";
                    const cloneCaption = cloneImg.closest('.splide__slide--clone')?.querySelector('.unsplash-caption');
                    if (cloneCaption) cloneCaption.style.opacity = "1";
                });

                if (loadingLabel) loadingLabel.remove();
            }
        }
    };

    // Se esperan TODAS para poder decidir si hace falta otro barrido. Cada `cargar` ya es
    // defensiva por dentro; el `catch` de aquí es para que una que reviente no impida saber el
    // resultado de las demás.
    const cargas = Promise.all(
        Array.from(targets).map(img => cargar(img).catch(e => {
            console.error('[Unsplash] fallo al resolver una imagen:', e);
            fallaron = true;
        }))
    ).then(() => {
        if (!fallaron || _rondasDiferidas >= _MAX_RONDAS_DIFERIDAS || _reintentoArmado) return;
        _reintentoArmado = true;
        setTimeout(() => {
            _reintentoArmado = false;
            _rondasDiferidas += 1;
            // Sin `await`: es una mejora de fondo, nadie la está esperando.
            resolverImagenesUnsplash({ diferido: true });
        }, _ESPERA_DIFERIDA_MS);
    });
    void cargas;

    // --- FONDOS (HERO con imagen de fondo) ---
    // Gradient separado de la imagen para que parallax.js solo mueva la foto.
    // El gradient va como --bg-overlay → CSS ::after lo renderiza fijo.
    bgTargets.forEach(async (el) => {
        const query = el.getAttribute('data-unsplash-bg');
        if (!query) return;
        
        // El velo de legibilidad (el `::after` de la sección, fijo: el parallax solo mueve la
        // foto) lo decide UNA regla, `utils/velo.js`, la misma que usan el fondo animado y los
        // paneles. Existe porque hay texto encima de la foto: lo pide quien lo necesita, el hero
        // emitiendo `data-bg-color` (su propio fondo) y cualquiera un `data-bg-gradient` a medida.
        // El banner v2 no pide el de color: su velo es su `.banner-overlay`. Sin ninguno de los
        // dos NO se pone velo — antes el respaldo era `'#000000'` y
        // `before-after` (que emite solo la query: sobre sus dos mitades no va ni una letra)
        // salía con un negro al 90 % encima de las DOS fotos.
        aplicarVelo(el);

        // Solo la imagen como background-image (parallax solo mueve esto)
        const applyBg = (imgUrl) => {
            el.style.backgroundImage = `url('${imgUrl}')`;
            el.style.backgroundSize = 'cover';
            el.style.backgroundPosition = 'center';
        };

        if (isResolved(query)) {
            applyBg(query);
            return;
        }

        const url = await _fetchFoto(query);
        if (url) {
            applyBg(url);
            // Persistir URL resuelta → al recargar, skip fetch
            el.setAttribute('data-unsplash-bg', url);
        }
    });
}

